import GameConfig from './GameConfig';
import * as Tone from 'tone';
import BackgroundMusic from './background-music';

export default class PhysicsPanel {
    constructor(scene) {
        this.scene = scene;
        this.soundEnabled = false;
        this.originalValues = {
            JUMP_THRUST: GameConfig.JUMP_THRUST,
            SPEED: GameConfig.SPEED,
            CLIMB_SPEED: GameConfig.CLIMB_SPEED,
            GRAVITY: this.scene.physics.world.gravity.y,
            COYOTE_TIME: GameConfig.COYOTE_TIME
        };
        this.createPanel();
        this.createToolbar();
        // Apply default muted state
        Tone.getDestination().mute = true;
    }

    createToolbar() {
        const toolbar = document.createElement('div');
        toolbar.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            display: flex;
            gap: 6px;
            z-index: 1000;
        `;

        const btnStyle = `
            background: rgba(0,0,0,0.7);
            color: white;
            border: 1px solid rgba(255,255,255,0.3);
            padding: 6px 10px;
            font-family: monospace;
            font-size: 14px;
            cursor: pointer;
            border-radius: 4px;
        `;

        this.soundBtn = document.createElement('button');
        this.soundBtn.textContent = '🔊';
        this.soundBtn.title = 'Toggle sound';
        this.soundBtn.style.cssText = btnStyle;
        this.soundBtn.onclick = () => this.toggleSound();

        this.panelBtn = document.createElement('button');
        this.panelBtn.textContent = '⚙';
        this.panelBtn.title = 'Toggle physics panel';
        this.panelBtn.style.cssText = btnStyle;
        this.panelBtn.onclick = () => this.togglePanel();

        toolbar.id = 'physics-toolbar';
        toolbar.appendChild(this.soundBtn);
        toolbar.appendChild(this.panelBtn);
        document.body.appendChild(toolbar);
    }

    toggleSound() {
        this.soundEnabled = !this.soundEnabled;
        Tone.getDestination().mute = !this.soundEnabled;
        if (this.soundEnabled) {
            if (!this.scene.backgroundMusic) {
                this.scene.backgroundMusic = new BackgroundMusic();
            }
            const music = this.scene.backgroundMusic;
            if (!music.initialized) {
                const midiFiles = {
                    square1: this.scene.cache.binary.get('square1'),
                    square2: this.scene.cache.binary.get('square2'),
                    triangle: this.scene.cache.binary.get('triangle'),
                    noise: this.scene.cache.binary.get('noise')
                };
                music.init(midiFiles).then(() => music.play());
            } else {
                music.play();
            }
            this.scene.isMusicPlaying = true;
        } else if (this.scene.backgroundMusic) {
            this.scene.backgroundMusic.pause();
            this.scene.isMusicPlaying = false;
        }
        this.soundBtn.textContent = this.soundEnabled ? '🔊' : '🔇';
    }

    togglePanel() {
        const panel = document.getElementById('physics-debug');
        if (panel) {
            panel.style.display = panel.style.display === 'none' ? 'flex' : 'none';
        }
    }

    createPanel() {
        const panel = document.createElement('div');
        panel.id = 'physics-debug';
        panel.style.cssText = `
            position: fixed;
            bottom: 56px;
            left: 0;
            width: 100%;
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 10px;
            font-family: monospace;
            display: none;
            gap: 20px;
        `;

        const controls = [
            { name: 'Gravity', min: 0, max: 2000, value: this.scene.physics.world.gravity.y, prop: 'GRAVITY' },
            { name: 'Jump Thrust', min: 0, max: 1000, value: GameConfig.JUMP_THRUST, prop: 'JUMP_THRUST' },
            { name: 'Speed', min: 0, max: 1000, value: GameConfig.SPEED, prop: 'SPEED' },
            { name: 'Climb Speed', min: 0, max: 500, value: GameConfig.CLIMB_SPEED, prop: 'CLIMB_SPEED' },
            { name: 'Coyote Time', min: 0, max: 500, value: GameConfig.COYOTE_TIME, prop: 'COYOTE_TIME' },
            { name: 'Lives', min: 0, max: 9, value: this.scene.statusPanel?.lives ?? 4, prop: 'LIVES' }
        ];

        controls.forEach(control => this.createControl(panel, control));

        this.createSlingshotToggle(panel);

        // Add reset all button
        const resetAll = document.createElement('button');
        resetAll.textContent = 'Reset All';
        resetAll.onclick = () => this.resetAll();
        panel.appendChild(resetAll);

        document.body.appendChild(panel);
    }

      createControl(panel, control) {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.flexDirection = 'column';
          container.style.alignItems = 'center';
          
          // Create elements without innerHTML overwrite
          const label = document.createElement('label');
          label.textContent = control.name;
          
          const valueDisplay = document.createElement('div');
          valueDisplay.id = `${control.prop}-value`;
          valueDisplay.textContent = control.value;
          
          const slider = document.createElement('input');
          slider.type = 'range';
          slider.id = `${control.prop}-slider`;
          slider.min = control.min;
          slider.max = control.max;
          slider.value = control.value;
          
          const reset = document.createElement('button');
          reset.textContent = '↺';
          reset.onclick = () => this.resetValue(control.prop);
      
          // Append in correct order
          container.appendChild(label);
          container.appendChild(valueDisplay);
          container.appendChild(slider);
          container.appendChild(reset);
          panel.appendChild(container);
      
          slider.addEventListener('input', (e) => {
              const value = parseInt(e.target.value);
              valueDisplay.textContent = value;
              this.updateValue(control.prop, value);
          });
      }
      
    createSlingshotToggle(panel) {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.alignItems = 'center';

        const label = document.createElement('label');
        label.textContent = 'Slingshot';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = 'slingshot-toggle';
        checkbox.checked = this.scene.hasSlingshot;
        checkbox.style.cssText = 'width: 18px; height: 18px; margin-top: 6px; cursor: pointer;';
        checkbox.addEventListener('change', (e) => {
            this.scene.hasSlingshot = e.target.checked;
            this.scene.slingshotAmmo = e.target.checked ? 99 : 0;
        });

        container.appendChild(label);
        container.appendChild(checkbox);
        panel.appendChild(container);
    }

      updateValue(prop, value) {
          if (prop === 'GRAVITY') {
              this.scene.physics.world.gravity.y = value;
          } else if (prop === 'LIVES') {
              this.scene.statusPanel.setLives(value);
          } else {
              GameConfig[prop] = value;
              GameConfig.updateValue(prop, value);
          }
          this.originalValues[prop] = value;
      }

    resetValue(prop) {
        const originalValue = this.originalValues[prop];
        const slider = document.querySelector(`input[type="range"]#${prop}-slider`);
        const display = document.querySelector(`#${prop}-value`);
        
        if (slider && display) {
            slider.value = originalValue;
            display.textContent = originalValue;
            this.updateValue(prop, originalValue);
        }
    }

    resetAll() {
        Object.entries(this.originalValues).forEach(([prop, value]) => {
            this.resetValue(prop);
        });
    }

    destroy() {
        const panel = document.getElementById('physics-debug');
        if (panel) panel.remove();
        const toolbar = document.getElementById('physics-toolbar');
        if (toolbar) toolbar.remove();
    }
}